import React from 'react';
import { bankAccountOptions, employmentOptions, guarantorOptions, seniorityOptions, tradeInOptions } from '../data/creditOptions';
import { ShieldCheckIcon } from 'lucide-react';
import { FormField, inputClass } from './FormField';
import { FormSection } from './FormSection';
import { SelectField } from './SelectField';
export type CreditProfile = {
  employment: string;
  income: string;
  seniority: string;
  bankAccount: string;
  guarantor: string;
  tradeIn: string;
};
export const emptyCreditProfile: CreditProfile = {
  employment: '',
  income: '',
  seniority: '',
  bankAccount: '',
  guarantor: '',
  tradeIn: ''
};
interface CreditProfileFieldsProps {
  values: CreditProfile;
  errors: Partial<Record<keyof CreditProfile, string>>;
  onChange: (key: keyof CreditProfile, value: string) => void;
}
function formatIncome(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  return digits ? new Intl.NumberFormat('es-AR').format(Number(digits)) : '';
}
export function CreditProfileFields({
  values,
  errors,
  onChange
}: CreditProfileFieldsProps) {
  return <FormSection icon={ShieldCheckIcon} title="Evaluación crediticia" description="Nos ayuda a ofrecerte el plan que mejor se adapte a vos. Es confidencial." data-id="element-218" data-mp="3bw4gwyqqot3g">
      <div className="grid gap-4 sm:grid-cols-2" data-id="element-219" data-mp="3kv9d3yetwacg">
        <SelectField id="cr-employment" label="Situación laboral" value={values.employment} options={employmentOptions} onChange={v => onChange('employment', v)} error={errors.employment} data-id="element-220" data-mp="3kugh9sher593" />
        <FormField label="Ingresos mensuales netos" htmlFor="cr-income" error={errors.income} data-id="element-221" data-mp="cg7vp4dazeqf">
          <div className="relative" data-id="element-222" data-mp="7gmwqty4v6dp">
            <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-muted" data-id="element-223" data-mp="7ho5usvl9iss">$</span>
            <input id="cr-income" inputMode="numeric" placeholder="850.000" className={`${inputClass} pl-7 tabular-nums`} value={values.income} onChange={e => onChange('income', formatIncome(e.target.value))} data-id="element-224" data-mp="2e95r97ygslf2" />
          </div>
        </FormField>
        <SelectField id="cr-seniority" label="Antigüedad laboral" value={values.seniority} options={seniorityOptions} onChange={v => onChange('seniority', v)} error={errors.seniority} data-id="element-225" data-mp="n315w387f7w5" />
        <SelectField id="cr-bank" label="¿Tenés cuenta bancaria?" value={values.bankAccount} options={bankAccountOptions} onChange={v => onChange('bankAccount', v)} error={errors.bankAccount} data-id="element-226" data-mp="w24qxcqxlxda" />
        <SelectField id="cr-guarantor" label="¿Contás con garante?" value={values.guarantor} options={guarantorOptions} onChange={v => onChange('guarantor', v)} error={errors.guarantor} data-id="element-227" data-mp="1asc1l6ap6c3f" />
        <SelectField id="cr-trade-in" label="¿Tenés un auto para permuta?" value={values.tradeIn} options={tradeInOptions} onChange={v => onChange('tradeIn', v)} error={errors.tradeIn} data-id="element-228" data-mp="34bc9xjqy6u9v" />
      </div>
    </FormSection>;
}
export function validateCreditProfile(values: CreditProfile): Partial<Record<keyof CreditProfile, string>> {
  const errors: Partial<Record<keyof CreditProfile, string>> = {};
  if (!values.employment) errors.employment = 'Elegí tu situación laboral';
  if (!values.income.replace(/\D/g, '')) errors.income = 'Ingresá tus ingresos mensuales';
  if (!values.seniority) errors.seniority = 'Elegí tu antigüedad';
  if (!values.bankAccount) errors.bankAccount = 'Elegí una opción';
  if (!values.guarantor) errors.guarantor = 'Elegí una opción';
  if (!values.tradeIn) errors.tradeIn = 'Elegí una opción';
  return errors;
}